/**
 * @lodex-pro/web-shared/auth/forced-password-change — FORCE_CHANGE_PASSWORD landing
 * screen for Cognito-provisioned operator portal users (DEC-152).
 *
 * Phase 15 followup (quick 260713-tsl) — ships the missing UI piece so an operator
 * who was provisioned via the Phase 15.2 admin-approval flow can set a permanent
 * password after their first sign-in.
 *
 * Flow:
 *   1. Amplify's signIn returns CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED.
 *   2. Consumer (e.g. web-portal SignInPage) fires `onNewPasswordRequired` from
 *      EmailTotpFlow and swaps this component into the tree.
 *   3. User enters a new password + confirmation → on submit, calls
 *      `confirmSignIn({ challengeResponse: newPassword })`.
 *   4. On success (`isSignedIn: true`), fires the `onCompleted` prop.
 *   5. On error, surfaces the mapped userMessage via `mapAmplifyError`.
 *
 * Consumer stylesheet notes:
 *   - Touch targets MUST be ≥ 56px (CLAUDE.md threshold) — this component uses
 *     semantic HTML only; the consumer stylesheet handles sizing.
 *   - Inputs use `type="password"` + `autoComplete="new-password"` so the browser
 *     knows not to fill with the current password.
 *
 * This component lives in `web-shared` so it is reusable by the Phase 16 disposal
 * portal login flow without duplication.
 *
 * Spec: master spec §7 (auth flows); CLAUDE.md touch-target + branding rules; DEC-152.
 */

import { useState, type FC } from 'react';
import { useForm, type SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { confirmSignIn } from 'aws-amplify/auth';

import { mapAmplifyError } from './error-mapper.js';
import { PasswordField } from './password-field.js';
import { newPasswordPairSchema, PASSWORD_RULES_TEXT } from './password-rules.js';

// ---------------------------------------------------------------------------
// Zod schema — mirrors Cognito default pool policy + client-side feedback
// ---------------------------------------------------------------------------

/**
 * Password policy: min 12 chars, upper + lower + digit + special char.
 * Matches the Cognito operator pool policy from cognito-admin.ts:84's
 * `temporaryPassword` shape. Cognito is authoritative server-side (per DEC-152
 * Grey Area 3); this provides faster client-side feedback for the common cases.
 */
// Quick 260827-pwd — the rules moved to password-rules.ts so the voluntary-change
// and forgot-password flows share ONE definition. Behaviour is unchanged; three
// copies of a regex list is how two screens end up disagreeing about what is valid.
const newPasswordSchema = newPasswordPairSchema;

type NewPasswordForm = z.infer<typeof newPasswordSchema>;

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

/**
 * Props for ForcedPasswordChange.
 *
 *   - `onCompleted` — fired after `confirmSignIn` returns `isSignedIn: true`.
 *     The consumer routes to the portal home (or wherever useAuth's Hub
 *     subscription will land the user).
 */
export interface ForcedPasswordChangeProps {
  readonly onCompleted: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * ForcedPasswordChange — handles the Cognito FORCE_CHANGE_PASSWORD challenge.
 *
 * Purely additive component; no router knowledge, no global state. Consumer
 * embeds this in their sign-in page and routes on the `onCompleted` callback.
 */
export const ForcedPasswordChange: FC<ForcedPasswordChangeProps> = ({ onCompleted }) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [succeeded, setSucceeded] = useState(false);

  const form = useForm<NewPasswordForm>({
    resolver: zodResolver(newPasswordSchema),
    defaultValues: { newPassword: '', confirmNewPassword: '' },
  });

  const onSubmit: SubmitHandler<NewPasswordForm> = async (values) => {
    setErrorMessage(null);
    try {
      const result = await confirmSignIn({ challengeResponse: values.newPassword });
      if (result.isSignedIn) {
        setSucceeded(true);
        onCompleted();
      } else {
        // Unexpected state: confirmSignIn returned but isSignedIn is false.
        // Surface as a generic error so the user can retry.
        setErrorMessage('Additional verification required. Try signing in again.');
      }
    } catch (err) {
      setErrorMessage(mapAmplifyError(err).userMessage);
    }
  };

  // Success terminal state — render a brief status while useAuth's Hub
  // subscription updates and the consumer navigates away.
  if (succeeded) {
    return <p role="status">Signing you in&hellip;</p>;
  }

  return (
    <form
      onSubmit={(e) => {
        void form.handleSubmit(onSubmit)(e);
      }}
      aria-label="Change password"
    >
      <PasswordField
        label="New password"
        id="fpc-new-password"
        autoComplete="new-password"
        hint={PASSWORD_RULES_TEXT}
        error={form.formState.errors.newPassword?.message}
        {...form.register('newPassword')}
      />
      <PasswordField
        label="Confirm new password"
        id="fpc-confirm-password"
        autoComplete="new-password"
        error={form.formState.errors.confirmNewPassword?.message}
        {...form.register('confirmNewPassword')}
      />

      <button type="submit" disabled={form.formState.isSubmitting}>
        Set password
      </button>

      {errorMessage !== null && <p role="alert">{errorMessage}</p>}
    </form>
  );
};
